import React from 'react';
import { Link } from 'react-router-dom';

import { api } from '../services/api';
import type { Operator, OperatorFilters as OperatorFiltersType } from '../services/types';

import { PageHeader } from '../ui/PageHeader';
import { Card } from '../ui/Card';
import { Modal } from '../ui/Modal';

export default function LocalOperatorsPage() {
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [operators, setOperators] = React.useState<Operator[]>([]);
  const [filters, setFilters] = React.useState<OperatorFiltersType>({
    region: 'All',
    language: 'All',
    experienceType: 'All',
    impactFocus: 'All',
  });
  const [reviewsOpen, setReviewsOpen] = React.useState(false);
  const [reviewsOperator, setReviewsOperator] = React.useState<Operator | null>(null);

  React.useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        setLoading(true);
        const res = await api.getOperators();
        if (!mounted) return;
        setOperators(res);
        setError(null);
      } catch (e) {
        if (!mounted) return;
        setError(e instanceof Error ? e.message : 'Failed to load operators');
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  const filtered = React.useMemo(() => {
    return operators.filter((op) => {
      if (filters.region !== 'All' && op.region !== filters.region) return false;
      if (filters.language !== 'All' && !op.languages.includes(filters.language)) return false;
      if (filters.experienceType !== 'All' && op.experienceType !== filters.experienceType) return false;
      if (filters.impactFocus !== 'All' && !op.impactHighlights.includes(filters.impactFocus)) return false;
      return true;
    });
  }, [operators, filters]);

  const uniqueRegions = React.useMemo(() => ['All', ...Array.from(new Set(operators.map((o) => o.region)))], [operators]);
  const uniqueTypes = React.useMemo(
    () => ['All', ...Array.from(new Set(operators.map((o) => o.experienceType)))],
    [operators]
  );
  const uniqueLanguages = React.useMemo(() => {
    const languages = new Set<string>();
    operators.forEach((op) => op.languages.forEach((language) => languages.add(language)));
    return ['All', ...Array.from(languages)];
  }, [operators]);
  const uniqueImpacts = React.useMemo(() => {
    const impacts = new Set<string>();
    operators.forEach((op) => op.impactHighlights.forEach((impact) => impacts.add(impact)));
    return ['All', ...Array.from(impacts)];
  }, [operators]);

  const updateFilter = (name: keyof OperatorFiltersType, value: string) => {
    setFilters((current) => ({ ...current, [name]: value }));
  };

  const openReviews = (op: Operator) => {
    setReviewsOperator(op);
    setReviewsOpen(true);
  };

  return (
    <div className="page heroPage">
      <div className="container">
        <PageHeader
          kicker="Local operators"
          title="Community-led travel, verified impact"
          subtitle="Filter by region and impact focus. Review what matters before booking."
        />

        {loading && <div className="muted">Loading operators...</div>}
        {!loading && error && <div className="badge badgeWarn">{error}</div>}

        {!loading && !error && (
          <div className="grid cols2" style={{ alignItems: 'start' }}>
            <Card title="Filters">
              <div className="formRow" style={{ gridTemplateColumns: '1fr' }}>
                <label>
                  <div className="label">Region</div>
                  <select className="input" value={filters.region} onChange={(e) => updateFilter('region', e.target.value)}>
                    {uniqueRegions.map((region) => (
                      <option key={region} value={region}>
                        {region}
                      </option>
                    ))}
                  </select>
                </label>

                <label>
                  <div className="label">Language</div>
                  <select
                    className="input"
                    value={filters.language}
                    onChange={(e) => updateFilter('language', e.target.value)}
                  >
                    {uniqueLanguages.map((language) => (
                      <option key={language} value={language}>
                        {language}
                      </option>
                    ))}
                  </select>
                </label>

                <label>
                  <div className="label">Experience type</div>
                  <select
                    className="input"
                    value={filters.experienceType}
                    onChange={(e) => updateFilter('experienceType', e.target.value)}
                  >
                    {uniqueTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </label>

                <label>
                  <div className="label">Impact focus</div>
                  <select
                    className="input"
                    value={filters.impactFocus}
                    onChange={(e) => updateFilter('impactFocus', e.target.value)}
                  >
                    {uniqueImpacts.map((impact) => (
                      <option key={impact} value={impact}>
                        {impact}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </Card>

            <div className="grid" style={{ gap: 14 }}>
              {filtered.length === 0 ? (
                <Card>
                  <div className="muted">No operators match those filters.</div>
                </Card>
              ) : (
                filtered.map((op) => (
                  <Card key={op.id} title={op.name}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      <img className="mediaImage" src={op.imageUrl} alt={op.name} />
                      <div className="muted">
                        {op.location} - {op.region}
                      </div>
                      <div>{op.communityOwnershipInfo}</div>

                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        <span className="badge">{op.experienceType}</span>
                        <span className="badge badgeAccent">
                          {op.averageRating.toFixed(1)} rating ({op.reviewCount} reviews)
                        </span>
                        {op.languages.map((language) => (
                          <span key={language} className="badge badgeAccent">
                            {language}
                          </span>
                        ))}
                        {op.impactHighlights.map((impact) => (
                          <span key={impact} className="badge">
                            {impact}
                          </span>
                        ))}
                      </div>

                      <div className="grid" style={{ gap: 10 }}>
                        {op.groupPackages.map((pkg) => (
                          <div key={pkg.id} className="card" style={{ boxShadow: 'none' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                              <div>
                                <div style={{ fontWeight: 900 }}>{pkg.title}</div>
                                <div className="muted">
                                  {pkg.duration} - ${pkg.basePrice} per seat
                                </div>
                              </div>
                              <Link className="btn btnPrimary" to={`/booking/${pkg.id}`}>
                                Book
                              </Link>
                            </div>
                          </div>
                        ))}
                      </div>

                      <button className="btn" type="button" onClick={() => openReviews(op)}>
                        Read reviews
                      </button>
                    </div>
                  </Card>
                ))
              )}
            </div>
          </div>
        )}

        <Modal
          isOpen={reviewsOpen}
          title={reviewsOperator ? `${reviewsOperator.name} reviews` : 'Reviews'}
          onClose={() => setReviewsOpen(false)}
        >
          <div className="grid" style={{ gap: 12 }}>
            {reviewsOperator?.reviews.map((review) => (
              <div key={review.id} className="card" style={{ boxShadow: 'none' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                  <strong>{review.reviewerName}</strong>
                  <span className="badge">{review.rating}/5</span>
                </div>
                <div className="muted" style={{ marginTop: 6 }}>
                  {review.dateISO}
                </div>
                <p style={{ marginBottom: 0 }}>{review.comment}</p>
              </div>
            ))}
          </div>
        </Modal>
      </div>
    </div>
  );
}
